'use client';

import { useCallback, useState } from 'react';
import dynamic from 'next/dynamic';
import { useCurrentUser } from '@/src/lib/hooks/query';
import { useScheduledToursForUser } from '@/src/lib/hooks/query/useHpToursQuery';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { analytics } from '@/src/components/features/property/lib';

// Interaction-only: the wizard renders after a Book Tour tap, so keep its
// chunk out of the eager HDP bundle. #book-tour auto-opens still work — the
// wizard mounts as soon as its chunk resolves.
const TourWizard = dynamic(() => import('./TourWizard').then((mod) => mod.TourWizard));

/**
 * Opens the App Router HDP instant-tour wizard. Legacy HALO drove this
 * through Redux; this version reads scheduled-tour state from React Query.
 */
export const useTourAction = (listing?: PropertyDetailListing | null, triggerLocation = 'home_details_halo') => {
  const { data: currentUser } = useCurrentUser();
  const scheduledToursQuery = useScheduledToursForUser(currentUser?.loggedIn ?? false);
  const { data: scheduledTours } = scheduledToursQuery;
  const isTourReady = currentUser !== undefined && (!currentUser?.loggedIn || scheduledToursQuery.isSuccess);
  const [isOpen, setIsOpen] = useState(false);
  const [isModifyingTour, setIsModifyingTour] = useState(false);
  const [hasOpenedTourWizard, setHasOpenedTourWizard] = useState(false);

  // `openTourFrom` exists for TourActionHost, which serves several callers that each name a
  // different origin for the same wizard. It is deliberately not folded into `openTour` as an
  // optional argument: `openTour` is handed straight to DOM `onClick` handlers, where that
  // signature would silently record a MouseEvent as the trigger location.
  const openTourFrom = useCallback(
    (from: string) => {
      if (!isTourReady) return;

      analytics.emitTrackTourClickEvent({
        triggerLocation: from,
        triggerObject: 'home_details_component|modal_tour_form',
        phase: 'open',
      });
      setIsModifyingTour(false);
      setHasOpenedTourWizard(true);
      setIsOpen(true);
    },
    [isTourReady],
  );

  const openTour = useCallback(() => openTourFrom(triggerLocation), [openTourFrom, triggerLocation]);

  const openTourReschedule = useCallback(() => {
    if (!isTourReady) return;

    analytics.emitTrackTourClickEvent({
      triggerLocation,
      triggerObject: 'home_details_component|modal_tour_form',
      phase: 'reschedule',
    });
    setIsModifyingTour(true);
    setHasOpenedTourWizard(true);
    setIsOpen(true);
  }, [isTourReady, triggerLocation]);

  const tourOverlay = hasOpenedTourWizard ? (
    <TourWizard
      open={isOpen}
      onOpenChange={setIsOpen}
      listing={listing}
      scheduledTours={scheduledTours}
      isModifyingTour={isModifyingTour}
    />
  ) : null;

  return { openTour, openTourFrom, openTourReschedule, tourOverlay, isTourReady };
};
